import { genders } from 'constants/campaign';
import { gigTypes } from 'constants/global';
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaignCreateDetails } from 'slices/campaigns/details/thunk';
import { AppDispatch, RootState } from 'store';
import { CampaignIdea } from 'types/campaign';
import { GigType, Milestone } from 'types/campaign/collaborations';

interface AICampaignIdeasProps {
    campaignIdeas: CampaignIdea[];
    onIdeaSelect: (idea: CampaignIdea) => void
    loading: boolean
}
const AICampaignIdeas: React.FC<AICampaignIdeasProps> = ({ campaignIdeas, onIdeaSelect, loading }) => {

    const [selectedIdea, setSelectedIdea] = useState<CampaignIdea | null>(null);

    const dispatch: AppDispatch = useDispatch();


    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns['create'])
    const goalList = campaignDetails?.goal_list ?? {};
    const gigTypesArr = campaignDetails?.gig_types ?? [];

    const gigTypes = useMemo(() => {
        return gigTypesArr.reduce((acc, gigType) => {
            acc[gigType.slug] = gigType;
            return acc;
        }, {} as Record<string, GigType>) ?? {};
    }, [gigTypesArr])

    useEffect(() => {
        if (selectedIdea) {
            onIdeaSelect(selectedIdea);
        }
    }, [selectedIdea]);


    useEffect(() => {
        dispatch(fetchCampaignCreateDetails());
    }, [])

    useEffect(() => {
        if (campaignIdeas.length > 0 && !selectedIdea) {
            setSelectedIdea(campaignIdeas[0]);
        }
    }, [campaignIdeas, selectedIdea]);

    const selected = useMemo(() => {
        return selectedIdea ?? campaignIdeas[0] ?? null;
    }, [selectedIdea, campaignIdeas]);

    const storylineSteps = useMemo(() => {
        const c = selected?.campaign;
        const sl = c?.story_line;
        return [
            { step: 1 as const, title: 'Hook', body: sl?.hook ?? '—' },
            { step: 2 as const, title: 'Value', body: sl?.value ?? '—' },
            { step: 3 as const, title: 'Bridge', body: sl?.bridge ?? '—' },
            { step: 4 as const, title: 'CTA', body: sl?.cta ?? c?.call_to_action ?? '—' },
        ];
    }, [selected]);

    const dosList = useMemo(() => selected?.campaign?.dos ?? [], [selected]);
    const dontsList = useMemo(() => selected?.campaign?.donts ?? [], [selected]);

    const audienceGenderLabel = useMemo(() => {
        const g = selected?.campaign?.target_audience_demography?.gender as keyof typeof genders | undefined;
        return g ? (genders[g] ?? g) : '—';
    }, [selected]);

    const audienceAgeGroups = useMemo(() => {
        const groups = selected?.campaign?.target_audience_demography?.age_groups ?? [];
        return groups.length ? groups.join(', ') : '—';
    }, [selected]);

    const creatorGenderLabel = useMemo(() => {
        const g = selected?.campaign?.creator_criteria?.gender as keyof typeof genders | undefined;
        return g ? (genders[g] ?? g) : '—';
    }, [selected]);

    const creatorAgeRange = useMemo(() => {
        const { min_age, max_age } = selected?.campaign?.creator_criteria ?? {};
        if (min_age && max_age) return `${min_age}-${max_age}`;
        if (min_age || max_age) return min_age ?? max_age ?? '—';
        return '—';
    }, [selected]);

    const creatorTierLabel = useMemo(() => {
        const b = selected?.campaign?.creator_criteria?.bucket?.trim();
        if (!b) return '—';
        return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
    }, [selected]);

    if (loading) return (
        <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
            <div className="loader mb-2"></div>
            <div className="mt-2 text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Fetching ideas...
            </div>
        </div>
    );

    return (
        <div className="campaign-ai-creation__grid">
            <div className="campaign-ai-creation__left" role="listbox" aria-label="Campaign ideas">
                {campaignIdeas.map((idea) => {
                    const selected = idea.uuid === selectedIdea?.uuid;
                    const goal = idea.campaign?.goal ?? '';
                    const category = idea.campaign?.category ?? '';
                    return (
                        <button
                            key={idea.uuid}
                            type="button"
                            className={`campaign-ai-creation-item${selected ? ' campaign-ai-creation-item--selected' : ''}`}
                            onClick={() => {
                                setSelectedIdea(idea);
                            }}
                            role="option"
                            aria-selected={selected}
                        >
                            <div className="campaign-ai-idea-item__metaRow">
                                {goal && <span className="campaign-ai-pill campaign-ai-pill--blue">{goalList[goal]?.label ?? goal}</span>}
                                {category && <span className="campaign-ai-pill">{gigTypes[category]?.name ?? category}</span>}
                            </div>
                            <div className="campaign-ai-creation-item__name">{idea.name}</div>
                            <div className="campaign-ai-creation-item__subline">{idea.campaign?.objective}</div>
                            <div className="campaign-ai-creation-item__criteria">
                                <span>{genders[idea.campaign?.target_audience_demography?.gender as keyof typeof genders] ?? '—'} · {idea.campaign.target_audience_demography?.age_groups.join(', ') ?? '—'}</span>
                                <span>{idea.campaign.creator_criteria.niche}</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="campaign-ai-creation__right" aria-label="Strategy details">
                {selected && (
                    <div className="campaign-ai-creation-detail">
                        <div>

                            <div className="campaign-ai-idea-item__metaRow">
                                {selected.campaign?.goal && <span className="campaign-ai-pill campaign-ai-pill--blue">{goalList[selected.campaign.goal]?.label ?? selected.campaign.goal}</span>}
                                {selected.campaign?.category && <span className="campaign-ai-pill">{gigTypes[selected.campaign.category]?.name ?? selected.campaign.category}</span>}
                            </div>
                            <div className="campaign-ai-creation-detail__title">{selected.name}</div>
                        </div>


                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">CREATORS BRIEF</div>
                            <div className="campaign-ai-field">
                                <div className="d-flex gap-4 flex-column">
                                    <span>{selected?.brief ?? "-"}</span>
                                    <div className="d-flex gap-3" aria-label="Criteria">
                                        <div className="campaign-ai-subcard justify-content-start w-50">
                                            <div className="campaign-ai-subcard__k">CALL TO ACTION</div>
                                            <div className="campaign-ai-subcard__v">{selected.campaign?.call_to_action ?? '—'}</div>
                                        </div>
                                        <div className="campaign-ai-subcard justify-content-start w-50">
                                            <div className="campaign-ai-subcard__k">HASHTAGS</div>
                                            <div className="campaign-ai-subcard__v">
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {(selected.campaign?.hashtag_suggestions ?? []).slice(0, 6).map((t) => (
                                                        <span key={t} className="campaign-ai-idea-tag">{t}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">STORYLINE</div>
                            <div className="campaign-ai-field campaign-ai-field--storyline">
                                <div className="campaign-ai-storyline-stepper" role="list" aria-label="Campaign storyline">
                                    {storylineSteps.map(({ step, title, body }, idx) => (
                                        <div key={step} className="campaign-ai-storyline-stepper__col" role="listitem">
                                            <div className="campaign-ai-storyline-stepper__rail" aria-hidden={true}>
                                                {idx === 0 ? (
                                                    <span className="campaign-ai-storyline-stepper__railSpacer" />
                                                ) : (
                                                    <span
                                                        className={`campaign-ai-storyline-stepper__connector campaign-ai-storyline-stepper__connector--head${idx === 1 ? '12' : idx === 2 ? '23' : '34'}`}
                                                    />
                                                )}
                                                <span className={`campaign-ai-storyline-stepper__bubble campaign-ai-storyline-stepper__bubble--${step}`}>{step}</span>
                                                {idx === 3 ? (
                                                    <span className="campaign-ai-storyline-stepper__railSpacer" />
                                                ) : (
                                                    <span
                                                        className={`campaign-ai-storyline-stepper__connector campaign-ai-storyline-stepper__connector--tail${idx === 0 ? '12' : idx === 1 ? '23' : '34'}`}
                                                    />
                                                )}
                                            </div>
                                            <div className="campaign-ai-storyline-stepper__colTitle">{title}</div>
                                            <p className="campaign-ai-storyline-stepper__colBody">{body}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">DO'S & DON'TS</div>
                            <div className="campaign-ai-field campaign-ai-field--dosdonts">
                                <div className="campaign-ai-dosdonts">
                                    <div className="campaign-ai-dosdonts__col">
                                        <h3 className="campaign-ai-dosdonts__heading campaign-ai-dosdonts__heading--do">DO&apos;S</h3>
                                        <ul className="campaign-ai-dosdonts__list">
                                            {dosList.length === 0 ? (
                                                <li className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--do campaign-ai-dosdonts__item--empty">—</li>
                                            ) : (
                                                dosList.map((line, idx) => (
                                                    <li key={`do-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--do">
                                                        {line}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </div>
                                    <div className="campaign-ai-dosdonts__col">
                                        <h3 className="campaign-ai-dosdonts__heading campaign-ai-dosdonts__heading--dont">DON&apos;TS</h3>
                                        <ul className="campaign-ai-dosdonts__list">
                                            {dontsList.length === 0 ? (
                                                <li className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--dont campaign-ai-dosdonts__item--empty">—</li>
                                            ) : (
                                                dontsList.map((line, idx) => (
                                                    <li key={`dont-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--dont">
                                                        {line}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">TARGETING</div>
                            <div className="campaign-ai-field campaign-ai-field--targeting">
                                <div className="campaign-ai-targeting">
                                    <div className="campaign-ai-targeting__col">
                                        <h3 className="campaign-ai-targeting__subheading">Audience Targeting</h3>
                                        <div className="campaign-ai-targeting__stack">
                                            <div className="campaign-ai-targeting__card">
                                                <div className="campaign-ai-targeting__row campaign-ai-targeting__row--audience">
                                                    <span className="campaign-ai-targeting__k">Gender</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--audience">{audienceGenderLabel}</span>
                                                </div>
                                            </div>
                                            <div className="campaign-ai-targeting__card">
                                                <div className="campaign-ai-targeting__row campaign-ai-targeting__row--audience">
                                                    <span className="campaign-ai-targeting__k">Age Group</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--audience">{audienceAgeGroups}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="campaign-ai-targeting__col">
                                        <h3 className="campaign-ai-targeting__subheading">Creator Criteria</h3>
                                        <div className="campaign-ai-targeting__card campaign-ai-targeting__card--creator">
                                            {(
                                                [
                                                    { k: 'Niche', v: selected.campaign?.creator_criteria?.niche ?? '—' },
                                                    { k: 'Tier', v: creatorTierLabel },
                                                    { k: 'Gender', v: creatorGenderLabel },
                                                    { k: 'Age', v: creatorAgeRange },
                                                    { k: 'Style', v: selected.campaign?.creator_criteria?.content_style ?? '—' },
                                                ] as const
                                            ).map((row, idx, arr) => (
                                                <div
                                                    key={row.k}
                                                    className={`campaign-ai-targeting__row campaign-ai-targeting__row--creator${idx < arr.length - 1 ? ' campaign-ai-targeting__row--creatorSep' : ''}`}
                                                >
                                                    <span className="campaign-ai-targeting__k">{row.k}</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--creator">{row.v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AICampaignIdeas